import { useCallback, useEffect, useState } from "react";
import axios from "axios";

import {
  Page,
  LegacyCard,
  IndexTable,
  Badge,
  Text,
  TextField,
  Pagination,
  Banner,
  EmptyState,
  Button,
  Tooltip,
  InlineStack,
  BlockStack,
  Box,
} from "@shopify/polaris";

import {
  SearchIcon,
  ExternalIcon,
  EmailIcon,
  ArrowRightIcon,
} from "@shopify/polaris-icons";

import { useAppBridge } from "../components/createContext/AppBridgeContext";
import { getAppBridgeToken } from "../utils/getAppBridgeToken";
import { TablePageSkeleton } from "../components/loading/PageSkeletons";
import { formatShopName } from "../components/Helper/Helper";

const PAGE_SIZE = 25;

const SORT_FIELDS = [
  null, // S.N. - not sortable
  null, // Shop Name - not sortable
  null, // Shop URL
  null, // Email
  null, // Phone
  null, // Status
  null, // Plan
  null, // Installed At - not sortable
  null, // Updated At
  null, // Action
];
const pad = (n) => String(n).padStart(2, "0");

// Reference format:
// date on the first line,
// clock time under it.
const splitDate = (value) => {
  if (!value) return { date: "—", time: "" };

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return { date: "—", time: "" };
  }

  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
  };
};

const planTone = (plan) => {
  if (plan.inTrial) return "attention";

  switch (String(plan.status).toLowerCase()) {
    case "active":
      return "success";

    case "trial":
      return "attention";

    case "expired":
    case "cancelled":
    case "uninstalled":
      return "critical";

    case "paused":
      return "warning";

    default:
      return "info";
  }
};

const planLabel = (plan) =>
  plan.inTrial ? `${plan.name} · Trial` : plan.name;

const storeHandle = (domain) =>
  String(domain || "").split(".")[0];

/*
 * Date cell
 * -------------------------
 * Date + time both centered
 */
const DateCell = ({ value }) => {
  const { date, time } = splitDate(value);

  return (
    <BlockStack gap="050" align="center">
      <Text as="span" variant="bodySm">
        {date}
      </Text>

      {time ? (
        <Text as="span" variant="bodySm" tone="subdued">
          {time}
        </Text>
      ) : null}
    </BlockStack>
  );
};

export default function SuperAdminArea() {
  const app = useAppBridge();

  const [shops, setShops] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
  });

  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");

  const [query, setQuery] = useState("");

  // Index 7 = "Installed At", the default sort column.
  const [sortIndex, setSortIndex] = useState(7);

  const [sortDirection, setSortDirection] =
    useState("descending");

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  /*
   * Load shops
   */
  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = await getAppBridgeToken(app);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_HOST}/super-admin/shops`,
        {
          params: {
            page,
            limit: PAGE_SIZE,
            search: query || undefined,
            sort: SORT_FIELDS[sortIndex] || undefined,
            order:
              sortDirection === "ascending"
                ? "asc"
                : "desc",
          },

          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setShops(res.data?.data || []);

      setPagination(
        res.data?.pagination || {
          page: 1,
          pages: 1,
          total: 0,
        }
      );
    } catch (err) {
      const status = err?.response?.status;

      setShops([]);

      setError(
        status === 403
          ? "You are not authorized to view this area."
          : err?.response?.data?.message ||
          "Failed to load shops."
      );
    } finally {
      setLoading(false);
    }
  }, [
    app,
    page,
    query,
    sortIndex,
    sortDirection,
  ]);

  /*
   * Load on changes
   */
  useEffect(() => {
    load();
  }, [load]);

  /*
   * Debounce search input
   */
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(t);
  }, [search]);

  /*
   * Table sorting
   */
  const handleSort = useCallback(
    (index, direction) => {
      if (!SORT_FIELDS[index]) return;

      setSortIndex(index);
      setSortDirection(direction);
      setPage(1);
    },
    []
  );

  /*
   * Table rows
   */
  const rows = shops.map((s, index) => {
    const rowIndex =
      (pagination.page - 1) * PAGE_SIZE +
      index +
      1;

    const domain = s.myshopifyDomain || s.shop;

    const handle = storeHandle(domain);

    return (
      <IndexTable.Row
        id={String(s._id)}
        key={String(s._id)}
        position={index}
      >
        {/* S.N. */}
        <IndexTable.Cell>
          <InlineStack
            align="center"
            blockAlign="center"
          >
            <Text
              as="span"
              variant="bodySm"
              tone="subdued"
            >
              {rowIndex}
            </Text>
          </InlineStack>
        </IndexTable.Cell>

        {/* SHOP NAME */}
        <IndexTable.Cell>
          <InlineStack
            align="center"
            blockAlign="center"
          >
            <Text
              as="span"
              variant="bodySm"
              fontWeight="semibold"
            >
              {formatShopName(
                s.shopName || handle
              )}
            </Text>
          </InlineStack>
        </IndexTable.Cell>

        {/* SHOP URL */}
        <IndexTable.Cell>
          {domain ? (
            <InlineStack
              align="center"
              blockAlign="center"
            >
              <Tooltip content={`Open ${domain}`}>
                <Button
                  icon={ExternalIcon}
                  variant="tertiary"
                  size="slim"
                  url={`https://${domain}`}
                  target="_blank"
                  accessibilityLabel={`Open storefront ${domain}`}
                />
              </Tooltip>
            </InlineStack>
          ) : (
            <InlineStack
              align="center"
              blockAlign="center"
            >
              <Text
                as="span"
                variant="bodySm"
                tone="subdued"
              >
                —
              </Text>
            </InlineStack>
          )}
        </IndexTable.Cell>

        {/* EMAIL */}
        <IndexTable.Cell>
          {s.email ? (
            <InlineStack
              align="center"
              blockAlign="center"
              gap="100"
              wrap={false}
            >
              <Text
                as="span"
                variant="bodySm"
              >
                {s.email}
              </Text>

              <Tooltip
                content={`Email ${s.email}`}
              >
                <Button
                  icon={EmailIcon}
                  variant="tertiary"
                  size="slim"
                  url={`mailto:${s.email}`}
                  accessibilityLabel={`Email ${s.email}`}
                />
              </Tooltip>
            </InlineStack>
          ) : (
            <InlineStack
              align="center"
              blockAlign="center"
            >
              <Text
                as="span"
                variant="bodySm"
                tone="subdued"
              >
                —
              </Text>
            </InlineStack>
          )}
        </IndexTable.Cell>

        {/* PHONE */}
        <IndexTable.Cell>
          <InlineStack
            align="center"
            blockAlign="center"
          >
            <Text
              as="span"
              variant="bodySm"
              tone="subdued"
            >
              {s.phone || "—"}
            </Text>
          </InlineStack>
        </IndexTable.Cell>

        {/* STATUS */}
        <IndexTable.Cell>
          <BlockStack
            gap="050"
            align="center"
          >
            {/* InlineStack prevents Badge from stretching */}
            <InlineStack
              align="center"
              blockAlign="center"
            >
              <Badge
                tone={
                  s.installed
                    ? "success"
                    : undefined
                }
              >
                {s.installed
                  ? "Installed"
                  : "Unistalled"}
              </Badge>
            </InlineStack>

            {/* <Text
              as="span"
              variant="bodySm"
              tone="subdued"
            >
              {s.appEnabled
                ? "App on"
                : "App off"}
            </Text> */}
          </BlockStack>
        </IndexTable.Cell>

        {/* PLAN */}
        <IndexTable.Cell>
          <InlineStack
            align="center"
            blockAlign="center"
          >
            <Badge tone={planTone(s.plan)}>
              {planLabel(s.plan)}
            </Badge>
          </InlineStack>
        </IndexTable.Cell>

        {/* INSTALLED AT */}

        <IndexTable.Cell>
          <InlineStack align="center" blockAlign="center">
            <DateCell value={s.installedAt} />
          </InlineStack>
        </IndexTable.Cell>

        {/* UPDATED AT */}
        {/* <IndexTable.Cell>
          <InlineStack align="center" blockAlign="center">
            <DateCell value={s.updatedAt} />
          </InlineStack>
        </IndexTable.Cell> */}

        {/* ACTION */}
        <IndexTable.Cell>
          {handle ? (
            <InlineStack
              align="center"
              blockAlign="center"
            >
              <Tooltip content="Open in Shopify admin">
                <Button
                  icon={ArrowRightIcon}
                  variant="tertiary"
                  size="slim"
                  url={`https://admin.shopify.com/store/${handle}`}
                  target="_blank"
                  accessibilityLabel={`Open ${handle} in Shopify admin`}
                />
              </Tooltip>
            </InlineStack>
          ) : (
            <InlineStack
              align="center"
              blockAlign="center"
            >
              <Text
                as="span"
                variant="bodySm"
                tone="subdued"
              >
                —
              </Text>
            </InlineStack>
          )}
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Page
    
      title="Shop Installations"
      subtitle={
        pagination.total
          ? `${pagination.total} shop${pagination.total === 1
            ? ""
            : "s"
          }`
          : undefined
      }
    >
      <LegacyCard>
        {/* SEARCH */}
        <Box
          padding="400"
          paddingBlockEnd="200"
        >
          <TextField
            label="Search shops"
            labelHidden
            placeholder="Search by store, domain, owner or email"
            value={search}
            onChange={setSearch}
            prefix={<SearchIcon />}
            autoComplete="off"
            clearButton
            onClearButtonClick={() =>
              setSearch("")
            }
          />
        </Box>

        {/* ERROR */}
        {error ? (
          <Box padding="400">
            <Banner
              tone="critical"
              title={error}
            />
          </Box>
        ) : loading ? (
          <TablePageSkeleton />
        ) : shops.length === 0 ? (
          /* EMPTY STATE */
          <EmptyState
            heading={
              query
                ? "No shops match your search"
                : "No shops installed yet"
            }
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>
              {query
                ? "Try a different store name, domain or email."
                : "Shops will appear here as merchants install the app."}
            </p>
          </EmptyState>
        ) : (
          /* TABLE */
          <IndexTable
            resourceName={{
              singular: "shop",
              plural: "shops",
            }}
            itemCount={shops.length}
            selectable={false}
            sortable={SORT_FIELDS.map(Boolean)}
            sortColumnIndex={sortIndex}
            sortDirection={sortDirection}
            onSort={handleSort}
            headings={[
              {
                title: "S.N.",
                alignment: "center",
              },
              {
                title: "Shop Name",
                alignment: "center",
              },
              {
                title: "Shop URL",
                alignment: "center",
              },
              {
                title: "Email",
                alignment: "center",
              },
              {
                title: "Phone",
                alignment: "center",
              },
              {
                title: "Status",
                alignment: "center",
              },
              {
                title: "Plan",
                alignment: "center",
              },
              {
                title: "Installed At",
                alignment: "center",
              },
              // {
              //   title: "Updated At",
              //   alignment: "center",
              // },
              {
                title: "Action",
                alignment: "center",
              },
            ]}
          >
            {rows}
          </IndexTable>
        )}

        {/* PAGINATION */}
        {!error &&
          pagination.pages > 1 ? (
          <Box padding="400">
            <InlineStack align="center">
              <Pagination
                hasPrevious={page > 1}
                onPrevious={() =>
                  setPage((p) =>
                    Math.max(1, p - 1)
                  )
                }
                hasNext={
                  page < pagination.pages
                }
                onNext={() =>
                  setPage((p) => p + 1)
                }
                label={`Page ${pagination.page} of ${pagination.pages}`}
              />
            </InlineStack>
          </Box>
        ) : null}
      </LegacyCard>
    </Page>
  );
}